import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./TutorialPage.module.css";
import { TutorialProvider, useTutorial } from "./TutorialContext";

// Import shadcn UI components
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../../../components/ui/Card";
import { Button } from "../../../components/ui/Button";

// Import Lucide icons
import {
    BarChart,
    Users,
    Video,
    Mic,
    SkipForward,
    List,
    UserSquare2,
} from "lucide-react";

// Inner component that uses the tutorial context
const TutorialPageContent: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { startTutorial } = useTutorial();

    // Get the interview data from location state
    const { interviewCase, sessionId } = location.state || {};

    const handleSkipTutorial = () => {
        // Navigate to the actual mock case page
        navigate(`/mock-case?interviewSessionId=${sessionId}`, {
            state: { interviewCase },
        });
    };

    const handleStartTutorial = () => {
        // First navigate to the mock case page
        navigate(`/mock-case?interviewSessionId=${sessionId}`, {
            state: {
                interviewCase,
                startTutorial: true, // Pass flag to start tutorial
            },
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-8">
                    <h1 className="text-3xl font-bold text-center text-gray-900 mb-6">
                        Mock Case Interview Tutorial
                    </h1>

                    <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-12">
                        Welcome to your mock case interview! Before you begin,
                        we'd like to familiarize you with the interface. This
                        will help you navigate your interview experience more
                        effectively.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                        <Card className="transition-all hover:shadow-lg">
                            <CardHeader className="pb-4">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                                    <List className="h-6 w-6 text-blue-600" />
                                </div>
                                <CardTitle>Interview Timeline</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-gray-600">
                                    Track your progress through different
                                    sections of the case interview. Each node
                                    represents a question or task you'll need to
                                    complete.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="transition-all hover:shadow-lg">
                            <CardHeader className="pb-4">
                                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                                    <UserSquare2 className="h-6 w-6 text-purple-600" />
                                </div>
                                <CardTitle>AI Interviewer</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-gray-600">
                                    Your AI interviewer will guide you through
                                    the case, ask questions, and provide
                                    feedback on your responses.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="transition-all hover:shadow-lg">
                            <CardHeader className="pb-4">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
                                    <div className="flex space-x-1">
                                        <Video className="h-5 w-5 text-green-600" />
                                        <Mic className="h-5 w-5 text-green-600" />
                                    </div>
                                </div>
                                <CardTitle>Interview Controls</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-gray-600">
                                    Use the camera and microphone buttons to
                                    manage your audio/video during the
                                    interview. You can also skip questions if
                                    needed.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="transition-all hover:shadow-lg">
                            <CardHeader className="pb-4">
                                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-2">
                                    <BarChart className="h-6 w-6 text-amber-600" />
                                </div>
                                <CardTitle>Data Visualization</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-gray-600">
                                    The interviewer may share charts, graphs,
                                    and data that you'll need to analyze as part
                                    of your case interview.
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                        <Button
                            variant="outline"
                            onClick={handleSkipTutorial}
                            className="px-6 py-2">
                            Skip Tutorial
                        </Button>
                        <Button
                            onClick={handleStartTutorial}
                            className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2">
                            Start Interactive Tutorial
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Wrapper component that provides the context
const TutorialPage: React.FC = () => {
    return (
        <TutorialProvider>
            <TutorialPageContent />
        </TutorialProvider>
    );
};

export default TutorialPage;
